import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { resizeImageToSquareJpeg } from '../utils/resizeImage';

export interface PhotoUploadModalProps {
    open: boolean;
    onSubmit: (photo: Blob) => void;
    onCancel: () => void;
}

const PREVIEW_SIZE = 256;

export default function PhotoUploadModal({ open, onSubmit, onCancel }: PhotoUploadModalProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [resized, setResized] = useState<Blob | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (open) return;
        setPreview(null);
        setResized(null);
        setError(null);
    }, [open]);

    async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        setError(null);
        setIsProcessing(true);
        try {
            const blob = await resizeImageToSquareJpeg(file, PREVIEW_SIZE);
            setResized(blob);
            setPreview(URL.createObjectURL(blob));
        } catch {
            setError('Could not process that image. Try a different file.');
        } finally {
            setIsProcessing(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={onCancel}
            title="Change photo"
            size="sm"
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col items-center gap-3">
                    {preview && (
                        <img
                            src={preview}
                            alt="Preview"
                            className="w-32 h-32 rounded-full object-cover border border-space-600"
                        />
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => { void handleFileChange(e); }}
                        className="text-body text-content-secondary file:mr-3 file:rounded-control file:border-0 file:bg-space-700 file:px-3 file:py-1.5 file:text-value file:text-content-primary hover:file:bg-space-600"
                    />
                    {isProcessing && <p className="text-micro text-content-muted">Processing image…</p>}
                    {error && <p className="text-micro text-status-danger">{error}</p>}
                </div>

                <div className="flex gap-3 justify-end mt-2">
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={!resized || isProcessing}
                        onClick={() => resized && onSubmit(resized)}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
